/**
 * ProShip Direct v2 — Direct against ProShip, one metric per row.
 *
 * WHY NOT THE SHARED COLUMN CHART: five grouped columns with labels like "Delivered by promised
 * date" cannot fit a narrow column. The labels collided and were clipped, the legend sat inside the
 * plot area, and neither is reachable from the call site — the shared component fixes its own axis
 * width and legend placement. Long category names want horizontal bars, and horizontal bars for
 * two series over a handful of metrics are a few Boxes.
 *
 * IT ALSO FIXES A HONESTY PROBLEM the chart had: delivery percentage and RTO percentage were
 * plotted on one axis, where taller looks better for both. It is not — a taller RTO bar is worse.
 * Here each block declares its direction and the gap is coloured against it, so a green number
 * always means "good for you" whichever way the metric runs.
 *
 * The axis is 0 to `scaleMax`, not zoomed to the data. Two bars at 90.4% and 93.2% genuinely are
 * nearly the same length, and stretching a 2.8 point gap across the full width to make it look
 * dramatic is the oldest chart lie there is. The gap number carries the precision instead.
 */
import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { SUBTLE, NAVY, BORDER, LIGHT_BG, SUCCESS, ERROR } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { MONO } from './directCellStyles';

const labelSx = { fontFamily: FONT, fontSize: SIZE['3xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const valueSx = { fontFamily: MONO, fontSize: SIZE['3xs'], color: NAVY };
const mutedSx = { fontFamily: MONO, fontSize: SIZE['3xs'], color: SUBTLE };
const captionSx = {
  fontFamily: FONT,
  fontSize: SIZE['4xs'],
  color: SUBTLE,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontWeight: WEIGHT.bold,
};

/** One bar. Zero-length values still show a sliver, so a row never looks like missing data. */
function Bar({ value, scaleMax, color, title }) {
  const pct = Math.max(1.5, Math.min(100, (Math.abs(Number(value) || 0) / scaleMax) * 100));
  return (
    <Tooltip title={title}>
      <Box sx={{ height: 7, borderRadius: 4, bgcolor: LIGHT_BG, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: 4 }} />
      </Box>
    </Tooltip>
  );
}

export default function ComparisonBars({ blocks, ownColor, proshipColor, format }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {blocks.map((block) => (
        <Box key={block.title}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
            <Typography sx={captionSx}>{block.title}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], color: SUBTLE }}>
              {block.note}
            </Typography>
          </Box>

          {block.rows.map((row) => {
            const scaleMax = row.scaleMax || block.scaleMax || 100;
            const diff = Number((row.own - row.proship).toFixed(2));
            const ownWins = block.betterIsHigh ? diff > 0 : diff < 0;
            const flat = Math.abs(diff) < 0.05;
            const gapColor = flat ? SUBTLE : ownWins ? SUCCESS : ERROR;
            const GapIcon = diff > 0 ? ArrowUpwardIcon : ArrowDownwardIcon;

            return (
              <Box
                key={row.label}
                sx={{
                  py: 0.85,
                  borderBottom: `1px solid ${BORDER}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <Typography sx={{ ...labelSx, flex: 1, minWidth: 0 }}>{row.label}</Typography>
                  <Typography sx={valueSx}>{format(row.own)}</Typography>
                  <Typography sx={mutedSx}>vs {format(row.proship)}</Typography>
                  {!flat && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', color: gapColor, minWidth: 52, justifyContent: 'flex-end' }}>
                      <GapIcon sx={{ fontSize: 11 }} />
                      <Typography sx={{ fontFamily: MONO, fontSize: SIZE['3xs'], fontWeight: WEIGHT.semibold, color: gapColor }}>
                        {Math.abs(diff)}
                      </Typography>
                    </Box>
                  )}
                  {flat && (
                    <Typography sx={{ ...mutedSx, minWidth: 52, textAlign: 'right' }}>level</Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <Bar value={row.own} scaleMax={scaleMax} color={ownColor} title={`Direct · ${format(row.own)}`} />
                  <Bar value={row.proship} scaleMax={scaleMax} color={proshipColor} title={`ProShip · ${format(row.proship)}`} />
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
